import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { from, of } from "rxjs";
import saveAs from "file-saver";

import { SearchBySkillOrConsultantPageActions } from "@skills-app/consultant-skill/actions";
import { ProfileService } from "@skills-app/services/profile.service";
import * as NotificationActions from "../../notifications/actions/notification.actions";

function safeString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function headerValue(err: HttpErrorResponse, name: string): string {
  return safeString(err?.headers?.get(name));
}

async function tryReadErrorMessage(
  err: HttpErrorResponse,
): Promise<string | null> {
  const body: unknown = err?.error;

  // If backend returns JSON normally (not as blob), handle it
  if (body && typeof body === "object") {
    const msg = (body as any)?.message;
    if (typeof msg === "string" && msg.trim().length) return msg.trim();
  }

  // With responseType: "blob", Angular typically gives us a Blob on error too
  if (body instanceof Blob) {
    try {
      const text = await body.text();
      if (!text) return null;

      // Attempt JSON parse first
      try {
        const parsed = JSON.parse(text);
        const msg = parsed?.message;
        if (typeof msg === "string" && msg.trim().length) return msg.trim();
      } catch {
        // Not JSON; fall back to raw text
      }

      return text.trim().length ? text.trim() : null;
    } catch {
      return null;
    }
  }

  return null;
}

@Injectable()
export class ProfileDownloadEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly profileService: ProfileService,
  ) {}

  downloadProfileDocx$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchBySkillOrConsultantPageActions.downloadProfile),
      switchMap(({ email, templateType, fileName }) =>
        this.profileService.downloadProfile(email, templateType).pipe(
          tap((blob) => {
            const safeBase = (fileName || "Consultant Profile")
              .trim()
              .replace(/\s+/g, " ")
              // literal <> to satisfy lint:no-entities
              .replace(/[\\/:*?"<>|]/g, "");

            const finalName = safeBase.toLowerCase().endsWith(".docx")
              ? safeBase
              : `${safeBase}.docx`;

            saveAs(blob, finalName);
          }),
          map(() =>
            SearchBySkillOrConsultantPageActions.downloadProfileSuccess(),
          ),
          catchError((err: unknown) => {
            const httpErr = err as HttpErrorResponse;

            const azureRef = headerValue(httpErr, "x-azure-ref");
            const middlewareId = headerValue(
              httpErr,
              "x-ms-middleware-request-id",
            );

            return from(tryReadErrorMessage(httpErr)).pipe(
              switchMap((serverMsg) => {
                const base =
                  serverMsg && serverMsg.length
                    ? `Download failed: ${serverMsg}`
                    : "Download failed: Unable to generate profile document.";

                const ids = [
                  middlewareId ? `reqId=${middlewareId}` : "",
                  azureRef ? `azureRef=${azureRef}` : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                const message = ids.length ? `${base} (${ids})` : base;

                return of(
                  SearchBySkillOrConsultantPageActions.downloadProfileError(),
                  NotificationActions.snackbarOpen({ message }),
                );
              }),
            );
          }),
        ),
      ),
    ),
  );
}
